import { useEffect, useState } from 'react';
import { api, type Chapter, type CardDetail as Detail } from '../lib/api';

/**
 * What this video publishes as.
 *
 * Shown the way YouTube shows it, because that is the only view in which the
 * real constraints are visible: a title is judged on the first forty characters
 * on a phone's home feed, and a description on the two lines above "more".
 * Counting characters in a plain textarea tells a creator nothing about either.
 *
 * Generation is a button, not the default. The pack is stored precisely so a
 * person can change it before the irreversible step — a screen that only ever
 * showed the model's output would make the model the author of what goes out
 * under their name.
 */

const TITLE_LIMIT = 100;
const FEED_TRUNCATION = 40;   // roughly what survives on a phone's home feed
const DESC_PREVIEW_LINES = 2; // what YouTube shows before "more"

function stamp(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}

export function SeoPanel({
  id, detail, onSaved,
}: { id: string; detail: Detail; onSaved: () => void }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [chapterError, setChapterError] = useState<string | null>(null);

  useEffect(() => {
    setTitle(detail.card.seo_title ?? '');
    setDescription(detail.card.seo_description ?? '');
    setTags((detail.card.seo_tags ?? []).join(', '));
    setDirty(false);
    setError(null);
  }, [detail.card.id, detail.card.seo_title, detail.card.seo_description]);

  // Lazily, and only once there is a render to have chapters from: working them
  // out reads the caption track out of the bucket, which does not belong on the
  // card detail response every drawer open pays for.
  useEffect(() => {
    if (detail.job?.status !== 'complete') { setChapters([]); setChapterError(null); return; }
    // The catch used to be setChapters([]), and the empty state below explains
    // that a video needs three marks ten seconds apart to show any. That is a
    // confident, specific reason — and it was printed whether or not it was the
    // real one. A request that failed said the video was too short.
    api.chapters(id)
      .then((c) => { setChapters(c); setChapterError(null); })
      .catch((e) => { setChapters([]); setChapterError((e as Error).message); });
  }, [id, detail.job?.status]);


  const tagList = tags.split(',').map((t) => t.trim()).filter(Boolean);
  // The real constraint is the joined length, not the count — see fitTags.
  const tagChars = tagList.join(',').length;
  const firstLines = description.split('\n').filter(Boolean).slice(0, DESC_PREVIEW_LINES);

  async function run(what: string, fn: () => Promise<unknown>) {
    setBusy(what); setError(null);
    try { await fn(); } catch (e) { setError((e as Error).message); } finally { setBusy(null); }
  }

  return (
    <section className="seo">
      <h4>Published as</h4>

      <div className="seoactions">
        <button
          className="btn"
          disabled={busy !== null}
          onClick={() => void run('write', async () => {
            const { seo } = await api.writeSeo(id);
            setTitle(seo.seo_title ?? '');
            setDescription(seo.seo_description ?? '');
            setTags((seo.seo_tags ?? []).join(', '));
            setDirty(false);
            onSaved();
          })}
        >{busy === 'write' ? 'Writing…' : detail.card.seo_title ? 'Rewrite' : 'Write it for me'}</button>

        <button
          className="btn primary"
          disabled={busy !== null || !dirty}
          onClick={() => void run('save', async () => {
            await api.saveSeo(id, {
              title: title.trim() || null,
              description: description.trim() || null,
              tags: tagList.length > 0 ? tagList : null,
            });
            setDirty(false);
            onSaved();
          })}
        >{busy === 'save' ? 'Saving…' : 'Save'}</button>
        {dirty && <span className="hint">Unsaved — the publisher uses what is saved.</span>}
      </div>

      {error && <p className="err">{error}</p>}

      <label className="seofield">
        Title
        <input
          type="text" value={title} maxLength={TITLE_LIMIT}
          onChange={(e) => { setTitle(e.target.value); setDirty(true); }}
          placeholder={detail.card.title}
        />
        <small className={title.length > TITLE_LIMIT - 10 ? 'warn' : undefined}>
          {title.length}/{TITLE_LIMIT}
          {title.length > FEED_TRUNCATION &&
            ` — a phone shows about the first ${FEED_TRUNCATION}`}
        </small>
      </label>

      <label className="seofield">
        Description
        <textarea
          rows={7} value={description}
          onChange={(e) => { setDescription(e.target.value); setDirty(true); }}
          placeholder="The concrete claim in the first two lines — they are all that shows before “more”."
        />
      </label>

      {/* The two lines that decide whether anyone expands it. */}
      {firstLines.length > 0 && (
        <div className="seopreview">
          <span className="label">Before “more”</span>
          {firstLines.map((l, i) => <p key={i}>{l}</p>)}
          {description.split('\n').filter(Boolean).length > DESC_PREVIEW_LINES && <p className="more">…more</p>}
        </div>
      )}

      <label className="seofield">
        Tags
        <input
          type="text" value={tags} spellCheck={false}
          onChange={(e) => { setTags(e.target.value); setDirty(true); }}
          placeholder="comma, separated, phrases someone would type"
        />
        <small className={tagChars > 500 ? 'warn' : undefined}>
          {tagList.length} tags · {tagChars}/500 characters
          {tagChars > 500 && ' — the ones past 500 are dropped, not shortened'}
        </small>
      </label>

      {/* Derived, never stored, so this is what would actually be published. */}
      <div className="chapters">
        <span className="label">Chapters</span>
        {chapterError ? (
          <p className="sub-dim" style={{ color: 'var(--red)' }}>
            Could not work out the chapters — {chapterError}. This is not the same as
            there being none.
          </p>
        ) : chapters.length === 0 ? (
          <p className="hint">
            None — a video needs at least three marks, ten seconds apart, to show any at all.
            These are worked out from the finished cut, so render first.
          </p>
        ) : (
          <ol>
            {chapters.map((c) => (
              <li key={c.at}><span className="at">{stamp(c.at)}</span>{c.title}</li>
            ))}
          </ol>
        )}
      </div>
    </section>
  );
}
